// frontend/src/pages/AdminVideosPage.tsx
import { useState, useEffect } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { PageLoader, LoadingSpinner } from '../components/LoadingSpinner';
import { useAdminVideos, getVideoSrc } from '../hooks/useFunVideos';
import {
  Film,
  Plus,
  Trash2,
  Clock,
  Check,
  X,
  RefreshCw,
  ArrowLeft,
  Pencil,
  ExternalLink,
} from 'lucide-react';

const ADMIN_EMAIL = 'a.burkov@gmail.com';

function EditableName({
  value,
  onSave,
}: {
  value: string;
  onSave: (newName: string) => Promise<boolean>;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    const trimmed = draft.trim();
    if (!trimmed || trimmed === value) {
      setDraft(value);
      setEditing(false);
      return;
    }
    setSaving(true);
    const ok = await onSave(trimmed);
    setSaving(false);
    if (ok) {
      setEditing(false);
    } else {
      setDraft(value);
      setEditing(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      setDraft(value);
      setEditing(false);
    }
  };

  if (editing) {
    return (
      <div className="flex items-center gap-1.5">
        <input
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleSave}
          disabled={saving}
          className="input text-sm py-0.5 px-2 h-7 min-w-0 flex-1"
        />
        {saving && <LoadingSpinner size="sm" />}
      </div>
    );
  }

  return (
    <button
      onClick={() => { setDraft(value); setEditing(true); }}
      className="group flex items-center gap-1.5 min-w-0 text-left"
      title="Click to edit name"
    >
      <span className="font-medium text-sm text-theme-text truncate">{value}</span>
      <Pencil className="h-3 w-3 text-theme-text-muted opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
    </button>
  );
}

export function AdminVideosPage() {
  const { user, loading: authLoading } = useAuth();
  const {
    videos,
    loading: videosLoading,
    generating: videoGenerating,
    pollingIds,
    lastPolledAt,
    generateVideo,
    updateVideoName,
    deleteVideo,
    refetch: refetchVideos,
  } = useAdminVideos();

  const [videoPrompt, setVideoPrompt] = useState('');
  const [deletingVideoId, setDeletingVideoId] = useState<string | null>(null);
  const [batchProgress, setBatchProgress] = useState<{ current: number; total: number } | null>(null);

  useEffect(() => {
    document.title = 'Admin Videos - Llambada';
    return () => { document.title = 'Llambada'; };
  }, []);

  // Parse non-empty prompt lines
  const promptLines = videoPrompt.split('\n').map(l => l.trim()).filter(Boolean);
  const promptCount = promptLines.length;

  // Tick every second so elapsed times update live
  const [, setTick] = useState(0);
  useEffect(() => {
    const hasGenerating = videos.some(v => v.status === 'generating');
    if (!hasGenerating) return;
    const timer = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(timer);
  }, [videos]);

  const formatElapsed = (dateStr: string) => {
    const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    if (minutes < 60) return `${minutes}m ${remainingSeconds}s`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${hours}h ${remainingMinutes}m`;
  };

  const formatLastChecked = (date: Date) => {
    const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
    if (seconds < 5) return 'just now';
    if (seconds < 60) return `${seconds}s ago`;
    return `${Math.floor(seconds / 60)}m ago`;
  };

  if (authLoading) return <PageLoader />;

  if (!user || user.email !== ADMIN_EMAIL) {
    return <Navigate to="/dashboard" replace />;
  }

  const readyCount = videos.filter(v => v.status === 'ready').length;
  const generatingCount = videos.filter(v => v.status === 'generating').length;
  const failedCount = videos.filter(v => v.status === 'failed').length;

  return (
    <Layout>
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <Link
            to="/admin"
            className="flex h-10 w-10 items-center justify-center rounded-lg bg-theme-surface hover:bg-theme-bg-muted transition-colors"
            title="Back to Admin"
          >
            <ArrowLeft className="h-5 w-5 text-theme-text-muted" />
          </Link>
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-purple-500/10 text-purple-500">
            <Film className="h-6 w-6" />
          </div>
          <div className="flex-1">
            <h1 className="font-display text-2xl font-bold text-theme-text">
              Fun Videos
            </h1>
            <p className="text-theme-text-secondary text-sm">
              Generate AI videos via Grok Imagine to display in agent message bubbles
            </p>
          </div>
          <button
            onClick={refetchVideos}
            disabled={videosLoading}
            className="btn btn-secondary btn-sm"
          >
            <RefreshCw className={`h-4 w-4 ${videosLoading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
          <div className="card p-4">
            <div className="text-theme-text-muted text-xs font-medium mb-1">Total</div>
            <div className="text-2xl font-bold text-theme-text">{videos.length}</div>
          </div>
          <div className="card p-4">
            <div className="text-theme-text-muted text-xs font-medium mb-1">Ready</div>
            <div className="text-2xl font-bold text-theme-success">{readyCount}</div>
          </div>
          <div className="card p-4">
            <div className="text-theme-text-muted text-xs font-medium mb-1">Generating</div>
            <div className="text-2xl font-bold text-amber-500">{generatingCount}</div>
          </div>
          <div className="card p-4">
            <div className="text-theme-text-muted text-xs font-medium mb-1">Failed</div>
            <div className="text-2xl font-bold text-theme-error">{failedCount}</div>
          </div>
        </div>

        {/* Generate New Videos Form */}
        <div className="card p-6 mb-8">
          <h3 className="text-sm font-semibold text-theme-text mb-4 flex items-center gap-2">
            <Plus className="h-4 w-4" />
            Generate New Videos
          </h3>
          <div className="space-y-3">
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-xs text-theme-text-muted">Prompts (one per line)</label>
                {promptCount > 0 && (
                  <span className="text-xs text-theme-text-muted">
                    {promptCount} video{promptCount !== 1 ? 's' : ''}
                  </span>
                )}
              </div>
              <textarea
                value={videoPrompt}
                onChange={(e) => setVideoPrompt(e.target.value)}
                placeholder={"A funny cat doing a moonwalk dance in a top hat\nPenguin riding a skateboard through a mall\nHamster playing a tiny grand piano"}
                className="input text-sm font-mono"
                rows={5}
                disabled={videoGenerating || batchProgress !== null}
              />
              <p className="text-[11px] text-theme-text-muted mt-1">
                Names are auto-generated from each prompt. You can edit them later.
              </p>
            </div>
            <button
              onClick={async () => {
                if (promptLines.length === 0) return;
                const lines = [...promptLines];
                setBatchProgress({ current: 0, total: lines.length });
                for (let i = 0; i < lines.length; i++) {
                  setBatchProgress({ current: i + 1, total: lines.length });
                  await generateVideo(lines[i]);
                }
                setBatchProgress(null);
                setVideoPrompt('');
              }}
              disabled={promptCount === 0 || videoGenerating || batchProgress !== null}
              className="btn bg-purple-500 hover:bg-purple-600 text-white w-full"
            >
              {batchProgress !== null ? (
                <>
                  <LoadingSpinner size="sm" />
                  Submitting {batchProgress.current}/{batchProgress.total}...
                </>
              ) : (
                <>
                  <Film className="h-4 w-4" />
                  {promptCount <= 1 ? 'Generate Video' : `Generate ${promptCount} Videos`}
                </>
              )}
            </button>
          </div>
        </div>

        {/* Videos List */}
        <div className="card p-6">
          <h3 className="text-sm font-semibold text-theme-text mb-4">
            All Videos ({videos.length})
          </h3>

          {videosLoading ? (
            <div className="flex justify-center py-12">
              <LoadingSpinner size="md" />
            </div>
          ) : videos.length === 0 ? (
            <div className="text-center py-12">
              <div className="inline-flex h-14 w-14 items-center justify-center rounded-xl bg-theme-surface mb-4">
                <Film className="h-7 w-7 text-theme-text-muted" />
              </div>
              <p className="text-theme-text-muted">
                No fun videos yet. Generate one above!
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {videos.map((video) => (
                <div
                  key={video.id}
                  className={`flex items-start gap-4 p-4 rounded-lg border transition-colors ${
                    video.status === 'ready'
                      ? 'bg-theme-success-bg/30 border-theme-success/30'
                      : video.status === 'failed'
                      ? 'bg-theme-error-bg/30 border-theme-error/30'
                      : 'bg-theme-surface/50 border-theme-border-muted'
                  }`}
                >
                  {/* Video preview */}
                  <div className="shrink-0 w-32 h-20 sm:w-40 sm:h-24 rounded-lg overflow-hidden bg-theme-surface flex items-center justify-center">
                    {video.status === 'ready' && getVideoSrc(video) ? (
                      <video
                        src={getVideoSrc(video)!}
                        className="w-full h-full object-cover"
                        muted
                        loop
                        playsInline
                        onMouseEnter={(e) => (e.target as HTMLVideoElement).play()}
                        onMouseLeave={(e) => { const v = e.target as HTMLVideoElement; v.pause(); v.currentTime = 0; }}
                      />
                    ) : video.status === 'generating' ? (
                      <RefreshCw className="h-6 w-6 text-theme-text-muted animate-spin" />
                    ) : video.status === 'failed' ? (
                      <X className="h-6 w-6 text-theme-error" />
                    ) : (
                      <Clock className="h-6 w-6 text-theme-text-muted" />
                    )}
                  </div>

                  {/* Video info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <EditableName
                        value={video.name}
                        onSave={(newName) => updateVideoName(video.id, newName)}
                      />
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium shrink-0 ${
                        video.status === 'ready'
                          ? 'bg-theme-success-bg text-theme-success'
                          : video.status === 'generating'
                          ? 'bg-amber-500/20 text-amber-600 dark:text-amber-400'
                          : video.status === 'failed'
                          ? 'bg-theme-error-bg text-theme-error'
                          : 'bg-theme-surface text-theme-text-muted'
                      }`}>
                        {video.status === 'ready' && <Check className="h-3 w-3" />}
                        {video.status === 'generating' && <RefreshCw className="h-3 w-3 animate-spin" />}
                        {video.status === 'failed' && <X className="h-3 w-3" />}
                        {video.status}
                      </span>
                    </div>
                    <p className="text-xs text-theme-text-muted mb-1.5">{video.prompt}</p>
                    <div className="flex items-center gap-3 text-xs text-theme-text-muted">
                      <span>{new Date(video.created_at).toLocaleDateString()}</span>
                      <span>{video.duration}s</span>
                      {video.status === 'ready' && getVideoSrc(video) && (
                        <a
                          href={getVideoSrc(video)!}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-accent-500 hover:underline"
                        >
                          <ExternalLink className="h-3 w-3" />
                          Open
                        </a>
                      )}
                    </div>
                    {video.status === 'generating' && (
                      <div className="flex items-center gap-3 mt-1.5">
                        <span className="text-xs text-amber-600 dark:text-amber-400">
                          Elapsed: {formatElapsed(video.created_at)}
                        </span>
                        {pollingIds.has(video.id) ? (
                          <span className="inline-flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400">
                            <span className="relative flex h-1.5 w-1.5">
                              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-75" />
                              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500" />
                            </span>
                            xAI confirmed still generating
                            {lastPolledAt.get(video.id) && (
                              <> &middot; checked {formatLastChecked(lastPolledAt.get(video.id)!)}</>
                            )}
                          </span>
                        ) : (
                          <span className="text-xs text-theme-text-muted">
                            Waiting to poll...
                          </span>
                        )}
                      </div>
                    )}
                    {video.error_message && (
                      <p className="text-xs text-theme-error mt-1">{video.error_message}</p>
                    )}
                  </div>

                  {/* Actions */}
                  <button
                    onClick={async () => {
                      setDeletingVideoId(video.id);
                      await deleteVideo(video.id);
                      setDeletingVideoId(null);
                    }}
                    disabled={deletingVideoId === video.id}
                    className="btn btn-secondary btn-sm shrink-0"
                    title="Delete video"
                  >
                    {deletingVideoId === video.id ? (
                      <LoadingSpinner size="sm" />
                    ) : (
                      <Trash2 className="h-4 w-4 text-theme-error" />
                    )}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}